/**
 * Agendas disponibles (administración, 14/10/2026)
 * ------------------------------------------------
 * Hasta hoy la disponibilidad de los doctores estaba en dos sitios:
 * «Disponibilidad Dres.» (8/10/2026: qué días viene cada doctor) y «Agendas
 * bloqueadas» (vacaciones y bloqueos de huecos). Se unifican aquí: el botón
 * de la Agenda se llama «Agendas disponibles» y «Agendas bloqueadas»
 * desaparece; todo lo suyo vive en esta página.
 *
 * Arriba, un desplegable de doctores y, al elegir uno, su calendario ANUAL con
 * todas sus agendas:
 *  - en VERDE los días que viene: claro la consulta, oscuro el quirófano y, con
 *    las dos cosas, mitad y mitad en diagonal;
 *  - en ROJO sus vacaciones, sus días libres y los tipos de agenda cerrados el
 *    día entero (con un tipo abierto y el otro cerrado, mitad y mitad); una
 *    raya roja abajo avisa de lo que solo cierra unas horas o una de varias
 *    agendas;
 *  - pinchar un día lleva a su agenda ese día.
 * En el modo edición, pinchar un día deja marcar la disponibilidad
 * (consulta, quirófano, las dos o ninguna, o volver al patrón), vacaciones o
 * día libre (hasta otra fecha), bloquear el día o unas horas de una agenda,
 * quitar un bloqueo o volver al patrón de bloqueo.
 *
 * Debajo: las vacaciones y días libres del año (se quitan de una vez), los
 * patrones —de disponibilidad («todos los martes, consulta») y de bloqueo
 * («todos los lunes por la tarde, bloqueado»)— y el formulario de bloquear
 * agendas de siempre (festivos, congresos, unas horas…), para una agenda,
 * varias o todas.
 *
 * El doctor elegido y el año van en la dirección (?profesional=…&anio=…): al
 * volver atrás desde la agenda se ve lo mismo que se dejó. La dirección vieja
 * (/disponibilidad-doctores) lleva aquí (App.js).
 *
 * Lo que cambió con la revisión del 14/10/2026: quitar un bloqueo de varios
 * días pregunta si solo ese día o todo el tramo (y siempre confirma, con el
 * tramo); quitar un día de un patrón es en UNA agenda; los patrones y
 * «bloquear el día» avisan de las citas como las vacaciones (el mismo cuadro,
 * `ConfirmarCitasDialog`); si otra persona ha cambiado algo entre medias, el
 * servidor contesta 409 y se vuelve a cargar; a unas vacaciones a las que les
 * falta el bloqueo de una agenda (dada de alta después) se les puede
 * «Completar el bloqueo»; lo ya pasado no se quita; y la lista de bloqueos de
 * abajo se vuelve a pedir tras cualquier cambio de la página.
 *
 * Quién: el personal; el servidor solo da lo que cada uno ve en la Agenda
 * (anestesia, el quirófano). Editar, gestión (el servidor lo dice con
 * `puede_editar`; bloquear agendas sueltas, `isGestion`). Las reglas:
 * backend/services/disponibilidad_doctores.py y agendas_disponibles.py.
 *
 * Los médicos (administración, 3/11/2026: «cada uno puede ver solo su propia
 * disponibilidad; no pueden ver la de sus compañeros») entran SOLO PARA VER:
 * el servidor les da únicamente los profesionales de sus agendas
 * (`doctor_user_id`) y les contesta 403 al escribir. Aquí, además, no se les
 * ofrece nada de escribir (ni el modo edición ni los botones de vacaciones,
 * patrones o bloqueos), se les abre su calendario directamente y, si no
 * tienen ninguna agenda propia, se les dice con texto.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  CalendarOff, CalendarRange, ChevronLeft, ChevronRight, Loader2, Lock, Pencil, Plus, Power, Trash2,
} from 'lucide-react';
import { mensajeError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isDoctor, isGestion } from '../lib/roles';
import { deleteBloqueo, listAgendas, strToDate, todayStr } from '../lib/citasApi';
import {
  DIAS_SEMANA, INICIAL_DIA, MESES, TEXTO_AUSENCIA, VERDE_CONSULTA, VERDE_QUIROFANO,
  agendasDelDia, aspectoDelDia, bloquearAgendas, cambiarDia, cambiarDiaPatronBloqueo,
  cargarBloqueos, cargarCalendario, citasPorConfirmar, completarAusencia, crearPatron, crearPatronBloqueo,
  desactivarPatron, editarPatron, editarPatronBloqueo, enlaceAgenda, esManual, horasMal,
  listarProfesionales, ponerAusencia, quitarAusencia, quitarPatronBloqueo, semanasDelMes,
  textoDias, textoFecha, textoHoras, textoTramo,
} from '../lib/disponibilidad';
import { PageHeader } from '../components/Common';
import BloqueosAgendaPanel from '../components/citas/BloqueosAgendaPanel';
import {
  AusenciaDialog, ConfirmarCitasDialog, DiaDialog, PatronBloqueoDialog, formPatronBloqueoVacio,
} from '../components/citas/AgendasDisponiblesFormularios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

const ANIO_MIN = 2000;
const ANIO_MAX = 2200;

function anioDeLaDireccion(valor, porDefecto) {
  const n = Number(valor);
  return Number.isInteger(n) && n >= ANIO_MIN && n <= ANIO_MAX ? n : porDefecto;
}

function Muestra({ clase, children, testId }) {
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={testId}>
      <span
        className={`inline-block w-4 h-4 rounded ${clase || 'border border-[#D9E3DD] bg-white'}`}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}

// Las horas del patrón (27/10/2026) son opcionales: vacías, todo el día.
const formVacio = (hoy, tipo) => ({
  id: null, tipo, dias: [], desde: hoy, hasta: '', nota: '', horaInicio: '', horaFin: '',
});

/** «09:00–14:00» de un patrón con horas, o ''. */
const horasDelPatron = (p) => (p.hora_inicio && p.hora_fin ? `${p.hora_inicio}–${p.hora_fin}` : '');

const conMayuscula = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

/**
 * La dirección vieja (/disponibilidad-doctores, 8/10/2026) lleva a la nueva
 * con lo que traiga (?profesional=…&anio=…): los enlaces guardados siguen
 * abriendo el mismo calendario.
 */
export function RedirigeAAgendasDisponibles() {
  const { search } = useLocation();
  return <Navigate to={`/agendas-disponibles${search || ''}`} replace />;
}

export default function AgendasDisponiblesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const gestion = isGestion(user);
  // Un médico: solo lo suyo y solo para ver (3/11/2026).
  const doctor = isDoctor(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const hoy = todayStr();
  const anioHoy = Number(hoy.slice(0, 4));
  const clave = searchParams.get('profesional') || '';
  const anio = anioDeLaDireccion(searchParams.get('anio'), anioHoy);

  const [profesionales, setProfesionales] = useState([]);
  const [cargandoPros, setCargandoPros] = useState(true);
  const [datos, setDatos] = useState(null); // respuesta de /calendario (disponibilidad)
  const [bloq, setBloq] = useState(null); // respuesta de /bloqueos (vacaciones y bloqueos)
  const [cargando, setCargando] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [editando, setEditando] = useState(false);
  const [diaElegido, setDiaElegido] = useState(null); // 'aaaa-mm-dd'
  const [ocupado, setOcupado] = useState(false); // algo del día guardándose
  const [form, setForm] = useState(null); // patrón de disponibilidad que se da de alta o se edita
  const [guardandoPatron, setGuardandoPatron] = useState(false);
  const [formPB, setFormPB] = useState(null); // patrón de bloqueo
  const [formAusencia, setFormAusencia] = useState(null); // {tipo, desde, hasta}
  // «Hay N citas esos días»: {detail, citas, textoSi, reintentar} hasta que
  // se confirma (`reintentar` repite lo mismo con `confirmar: true`).
  const [confirmacion, setConfirmacion] = useState(null);
  // Todas las agendas, para bloquear agendas sueltas (gestión).
  const [todasLasAgendas, setTodasLasAgendas] = useState([]);

  useEffect(() => {
    let cancelado = false;
    listarProfesionales()
      .then((lista) => { if (!cancelado) setProfesionales(lista); })
      .catch((e) => { if (!cancelado) toast.error(mensajeError(e, 'No se pudieron cargar los doctores')); })
      .finally(() => { if (!cancelado) setCargandoPros(false); });
    return () => { cancelado = true; };
  }, []);

  useEffect(() => {
    if (!gestion) return undefined;
    let cancelado = false;
    listAgendas()
      .then((lista) => { if (!cancelado && Array.isArray(lista)) setTodasLasAgendas(lista); })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [gestion]);

  // El doctor de la dirección, si es de los que se pueden ver.
  const elegido = useMemo(
    () => profesionales.find((p) => p.clave === clave) || null,
    [profesionales, clave],
  );

  useEffect(() => {
    if (!elegido) { setDatos(null); setBloq(null); return undefined; }
    let cancelado = false;
    setCargando(true);
    Promise.all([
      cargarCalendario(elegido.clave, anio),
      // Si fallan los bloqueos, la disponibilidad se pinta igual.
      cargarBloqueos(elegido.clave, anio).catch((e) => {
        if (!cancelado) toast.error(mensajeError(e, 'No se pudieron cargar las vacaciones y los bloqueos'));
        return null;
      }),
    ])
      .then(([d, b]) => { if (!cancelado) { setDatos(d); setBloq(b); } })
      .catch((e) => {
        if (cancelado) return;
        setDatos(null);
        setBloq(null);
        toast.error(mensajeError(e, 'No se pudo cargar el calendario'));
      })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [elegido, anio, recarga]);

  const recargar = useCallback(() => setRecarga((n) => n + 1), []);

  const irA = useCallback((nuevaClave, nuevoAnio) => {
    const params = {};
    if (nuevaClave) params.profesional = nuevaClave;
    if (nuevoAnio !== anioHoy) params.anio = String(nuevoAnio);
    setSearchParams(params, { replace: true });
  }, [anioHoy, setSearchParams]);

  // Un médico con una sola ficha (lo normal: la suya) la ve abierta sin tener
  // que elegirla. El servidor ya solo le manda la suya.
  useEffect(() => {
    if (doctor && !cargandoPros && !elegido && profesionales.length === 1) {
      irA(profesionales[0].clave, anio);
    }
  }, [doctor, cargandoPros, elegido, profesionales, irA, anio]);

  // Lo que llegó del servidor, solo si es de este doctor y este año: mientras
  // carga otro, no se pintan los días del anterior en la rejilla nueva.
  const vigente = datos && elegido && datos.profesional?.clave === elegido.clave && datos.anio === anio
    ? datos : null;
  const vigenteBloq = bloq && elegido && bloq.profesional?.clave === elegido.clave && bloq.anio === anio
    ? bloq : null;
  // Lo que se ve del profesional: lo que manda el servidor (solo las agendas
  // que esta persona puede ver) o, mientras carga, lo del desplegable.
  const profesional = vigente?.profesional || elegido;
  // Un médico nunca edita aquí, diga lo que diga la respuesta.
  const puedeEditar = !doctor && !!vigente?.puede_editar;
  const tieneConsulta = (profesional?.consulta || []).length > 0;
  const tieneQuirofano = (profesional?.quirofano || []).length > 0;
  const dias = vigente?.dias || {};
  const diasBloq = vigenteBloq?.dias || {};
  const patrones = vigente?.patrones || [];
  const patronesBloqueo = vigenteBloq?.patrones || [];
  const ausencias = vigenteBloq?.ausencias || [];

  const resumen = useMemo(() => {
    let consulta = 0;
    let quirofano = 0;
    let fuera = 0;
    // Un día de vacaciones, o con ese tipo cerrado el día entero, no cuenta.
    Object.entries(vigente?.dias || {}).forEach(([f, d]) => {
      const b = vigenteBloq?.dias?.[f];
      if (b?.ausencia) return;
      if (d.consulta && !b?.consulta_bloqueada) consulta += 1;
      if (d.quirofano && !b?.quirofano_bloqueada) quirofano += 1;
    });
    Object.values(vigenteBloq?.dias || {}).forEach((d) => { if (d.ausencia) fuera += 1; });
    return { consulta, quirofano, fuera };
  }, [vigente, vigenteBloq]);

  // ------------------------------------------------------------- acciones

  /** Tras guardar algo: aviso, se cierra el día (y el aviso de citas) y se
   *  vuelve a pedir el año (y la lista de bloqueos de abajo). */
  const hecho = (texto) => {
    toast.success(texto);
    setConfirmacion(null);
    setDiaElegido(null);
    recargar();
  };

  /**
   * Un 409 del servidor. Si es «hay citas esos días», se pregunta con el
   * cuadro de siempre y `reintentar` lo repite con `confirmar`. Si es «otra
   * persona lo ha cambiado», se dice y se vuelve a cargar para ver cómo ha
   * quedado. Devuelve true si lo ha atendido.
   */
  const atender409 = (e, textoSi, reintentar) => {
    const citas = citasPorConfirmar(e);
    if (citas) {
      setConfirmacion({ ...citas, textoSi, reintentar });
      return true;
    }
    if (e?.response?.status === 409) {
      toast.error(mensajeError(e, 'Otra persona lo ha cambiado: vuelve a cargar'));
      setConfirmacion(null);
      recargar();
      return true;
    }
    return false;
  };
  const SIN_ANULAR = { marcar: 'Marcar igualmente (sin anular citas)', bloquear: 'Bloquear igualmente (sin anular citas)' };
  /** Tras un fallo: el 409 (citas u «otra persona»), o el mensaje. Si ya se
   *  había confirmado, otro «hay citas» no vuelve a preguntar. */
  const fallo = (e, texto, textoSi = null, reintentar = null, yaConfirmado = false) => {
    if (yaConfirmado && citasPorConfirmar(e)) toast.error(mensajeError(e, texto));
    else if (!atender409(e, textoSi, reintentar)) toast.error(mensajeError(e, texto));
  };

  const pincharDia = (fecha) => {
    if (editando) { setDiaElegido(fecha); return; }
    const ids = agendasDelDia(dias[fecha], profesional, diasBloq[fecha]);
    if (!ids.length) {
      toast.error('No hay ninguna agenda activa de ese tipo para abrir');
      return;
    }
    navigate(enlaceAgenda(fecha, ids));
  };

  /** `horas`: las de cada tipo marcado ({consulta_hora_inicio, …}), o null:
   *  todo el día. */
  const ponerDia = async (consulta, quirofano, horas = null) => {
    if (!diaElegido || !profesional) return;
    setOcupado(true);
    try {
      const fila = await cambiarDia(profesional.clave, diaElegido, consulta, quirofano, horas);
      setDatos((prev) => {
        if (!prev) return prev;
        const siguientes = { ...prev.dias };
        const { fecha, ...resto } = fila || {};
        if (resto.consulta || resto.quirofano || resto.consulta_manual || resto.quirofano_manual) {
          siguientes[fecha || diaElegido] = resto;
        } else {
          delete siguientes[fecha || diaElegido];
        }
        return { ...prev, dias: siguientes };
      });
      toast.success(`${textoFecha(diaElegido)}: guardado`);
      setDiaElegido(null);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo guardar el día'));
    } finally {
      setOcupado(false);
    }
  };

  /** Vacaciones, día libre o quitarlos (tipo null). Si hay citas esos días,
   *  el servidor no marca nada y se pregunta (`ConfirmarCitasDialog`). */
  const guardarAusencia = async (cuerpo) => {
    setOcupado(true);
    try {
      await ponerAusencia(cuerpo);
      const tramo = textoTramo(cuerpo.desde, cuerpo.hasta);
      setFormAusencia(null);
      hecho(cuerpo.tipo ? `${TEXTO_AUSENCIA[cuerpo.tipo]}: ${tramo}` : `Quitado: ${tramo}`);
    } catch (e) {
      fallo(e, 'No se pudo guardar', SIN_ANULAR.marcar, () => guardarAusencia({ ...cuerpo, confirmar: true }),
        cuerpo.confirmar);
    } finally {
      setOcupado(false);
    }
  };

  const ausenciaDelDia = (tipo, hasta) => guardarAusencia({
    profesional: profesional.clave, desde: diaElegido, hasta: hasta || diaElegido, tipo,
  });

  /** «Bloquear el día» (o unas horas) de una agenda. Con citas, se pregunta. */
  const bloquearDia = async ({ agendaId, diaEntero, start, end, motivo }, confirmar = false) => {
    setOcupado(true);
    const datos = { agendaId, diaEntero, start, end, motivo };
    try {
      await bloquearAgendas({
        agendaIds: [agendaId], fechas: [diaElegido], motivo, ...(diaEntero ? {} : { start, end }), confirmar,
      });
      hecho(diaEntero ? `${textoFecha(diaElegido)}: agenda bloqueada` : `${textoFecha(diaElegido)}: bloqueado de ${start} a ${end}`);
    } catch (e) {
      fallo(e, 'No se pudo bloquear', SIN_ANULAR.bloquear, () => bloquearDia(datos, true), confirmar);
    } finally {
      setOcupado(false);
    }
  };

  /**
   * Quitar un bloqueo desde el cuadro del día (`modo`: 'dia' solo ese día,
   * 'todo' el bloqueo entero). Uno suelto se confirma siempre, diciendo el
   * tramo; uno de varios días, si es «solo este día», el servidor recorta el
   * tramo. El de un patrón quita ese día SOLO en su agenda, y se deshace al
   * momento con «Volver al patrón».
   */
  const quitarBloqueo = async (b, modo = 'todo') => {
    const agenda = b.agenda || 'la agenda';
    const tramo = b.date_from && b.date_to && b.date_from !== b.date_to;
    const soloElDia = !b.patron_id && tramo && modo === 'dia';
    if (!b.patron_id) {
      const pregunta = soloElDia
        ? `¿Quitar el bloqueo de ${agenda} solo el ${textoFecha(diaElegido)}? El resto del tramo (${textoTramo(b.date_from, b.date_to)}) sigue bloqueado.`
        : `¿Quitar el bloqueo de ${agenda}${tramo ? ` de todo el tramo, del ${textoFecha(b.date_from)} al ${textoFecha(b.date_to)}` : ` del ${textoFecha(b.date_from || diaElegido)}`}${b.reason ? ` (${b.reason})` : ''}? La agenda vuelve a quedar abierta ${tramo ? 'esos días' : 'ese día'}.`;
      if (!window.confirm(pregunta)) return;
    }
    setOcupado(true);
    try {
      if (b.patron_id) await cambiarDiaPatronBloqueo(b.patron_id, diaElegido, false, { agendaId: b.agenda_id });
      else if (soloElDia) await deleteBloqueo(b.id, { fecha: diaElegido });
      else await deleteBloqueo(b.id);
      if (b.patron_id) hecho(`${textoFecha(diaElegido)}: quitado del patrón en ${agenda}`);
      else if (soloElDia) hecho(`${textoFecha(diaElegido)}: bloqueo quitado ese día`);
      else hecho('Bloqueo quitado');
    } catch (e) {
      fallo(e, 'No se pudo quitar el bloqueo');
    } finally {
      setOcupado(false);
    }
  };

  const volverAlPatronDeBloqueo = async (patronId, agendaId = null, confirmar = false) => {
    setOcupado(true);
    try {
      await cambiarDiaPatronBloqueo(patronId, diaElegido, true, { agendaId, confirmar });
      hecho(`${textoFecha(diaElegido)}: vuelve al patrón`);
    } catch (e) {
      fallo(e, 'No se pudo volver al patrón', SIN_ANULAR.bloquear,
        () => volverAlPatronDeBloqueo(patronId, agendaId, true), confirmar);
    } finally {
      setOcupado(false);
    }
  };

  /** Quita unas vacaciones enteras de hoy en adelante: lo pasado se queda. */
  const quitarAusenciaEntera = async (a) => {
    const que = `${TEXTO_AUSENCIA[a.ausencia]?.toLowerCase() || 'la ausencia'} del ${textoTramo(a.desde, a.hasta)}`;
    const texto = a.desde < hoy
      ? `¿Quitar ${que}? Se quitan de hoy en adelante; lo que ya pasó se queda como estaba. Sus agendas vuelven a quedar abiertas esos días.`
      : `¿Quitar ${que}? Sus agendas vuelven a quedar abiertas esos días.`;
    if (!window.confirm(texto)) return;
    try {
      await quitarAusencia(a.grupo_id);
      hecho(`Quitado: ${textoTramo(a.desde < hoy ? hoy : a.desde, a.hasta)}`);
    } catch (e) {
      fallo(e, 'No se pudo quitar');
    }
  };

  /** «Completar el bloqueo»: a las agendas que les falta (dadas de alta
   *  después). Con citas esos días, se pregunta. */
  const completarBloqueo = async (a, confirmar = false) => {
    setOcupado(true);
    try {
      const r = await completarAusencia(a.grupo_id, { confirmar });
      hecho(r?.creados ? `Bloqueo completado: ${(r.agendas || []).join(', ')}` : 'Ya estaba completo');
    } catch (e) {
      fallo(e, 'No se pudo completar el bloqueo', SIN_ANULAR.bloquear, () => completarBloqueo(a, true), confirmar);
    } finally {
      setOcupado(false);
    }
  };

  // Patrones de disponibilidad.
  const abrirAlta = () => setForm(formVacio(hoy, tieneConsulta ? 'consulta' : 'quirofano'));
  const abrirEdicion = (p) => setForm({
    id: p.id, tipo: p.tipo, dias: [...(p.dias_semana || [])], desde: p.desde || '',
    hasta: p.hasta || '', nota: p.nota || '', horaInicio: p.hora_inicio || '', horaFin: p.hora_fin || '',
  });
  const alternarDiaSemana = (d) => setForm((f) => ({
    ...f, dias: f.dias.includes(d) ? f.dias.filter((x) => x !== d) : [...f.dias, d].sort((a, b) => a - b),
  }));

  const guardarPatron = async () => {
    if (!form || !profesional) return;
    if (!form.dias.length) { toast.error('Marca al menos un día de la semana'); return; }
    if (!form.desde) { toast.error('Indica desde qué fecha vale el patrón'); return; }
    if (form.hasta && form.hasta < form.desde) { toast.error('La fecha «hasta» no puede ser anterior a «desde»'); return; }
    if (horasMal(form.horaInicio, form.horaFin)) {
      toast.error('Pon la hora de inicio y la de fin (o ninguna: todo el día), con la de fin después');
      return;
    }
    const cuerpo = {
      tipo: form.tipo, dias_semana: form.dias, desde: form.desde,
      hasta: form.hasta || null, nota: form.nota.trim(),
      hora_inicio: form.horaInicio || null, hora_fin: form.horaFin || null,
    };
    setGuardandoPatron(true);
    try {
      if (form.id) await editarPatron(form.id, cuerpo);
      else await crearPatron({ ...cuerpo, profesional: profesional.clave });
      toast.success(form.id ? 'Patrón guardado' : 'Patrón añadido: los días se han rellenado');
      setForm(null);
      recargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo guardar el patrón'));
    } finally {
      setGuardandoPatron(false);
    }
  };

  const activarPatron = async (p, activo) => {
    try {
      if (activo) {
        await editarPatron(p.id, {
          tipo: p.tipo, dias_semana: p.dias_semana, desde: p.desde, hasta: p.hasta || null,
          nota: p.nota || '', hora_inicio: p.hora_inicio || null, hora_fin: p.hora_fin || null,
          activo: true,
        });
      } else {
        await desactivarPatron(p.id);
      }
      toast.success(activo ? 'Patrón activado' : 'Patrón desactivado');
      recargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo cambiar el patrón'));
    }
  };

  // Patrones de bloqueo.
  const abrirAltaPB = () => setFormPB(formPatronBloqueoVacio(hoy, profesional));
  const abrirEdicionPB = (p) => setFormPB({
    id: p.id, agendas: [...(p.agenda_ids || [])], dias: [...(p.dias_semana || [])],
    desde: p.desde || '', hasta: p.hasta || '', diaEntero: p.all_day !== false,
    start: p.start || '15:00', end: p.end || '20:00', motivo: p.motivo || '',
    // Lo que se leyó: si otra persona lo cambia mientras tanto, el servidor
    // contesta 409 y no se pisa.
    actualizadoAt: p.actualizado_at || null,
  });
  const cuerpoPB = (f, extra = {}) => ({
    agenda_ids: f.agendas, dias_semana: [...f.dias].sort((a, b) => a - b), desde: f.desde,
    hasta: f.hasta, all_day: f.diaEntero, start: f.diaEntero ? null : f.start,
    end: f.diaEntero ? null : f.end, motivo: f.motivo.trim(), ...extra,
  });

  const guardarPB = async (confirmar = false) => {
    if (!formPB || !profesional) return;
    setOcupado(true);
    const extra = confirmar ? { confirmar: true } : {};
    try {
      if (formPB.id) {
        await editarPatronBloqueo(formPB.id, cuerpoPB(formPB, { actualizado_at: formPB.actualizadoAt || null, ...extra }));
      } else {
        await crearPatronBloqueo({ ...cuerpoPB(formPB, extra), profesional: profesional.clave });
      }
      toast.success(formPB.id ? 'Patrón de bloqueo guardado' : 'Patrón de bloqueo añadido: los días se han bloqueado');
      setFormPB(null);
      setConfirmacion(null);
      recargar();
    } catch (e) {
      // «Otra persona lo ha cambiado»: lo que hay en el cuadro ya no vale; se
      // cierra y se vuelve a cargar (atender409).
      if (!citasPorConfirmar(e) && e?.response?.status === 409) setFormPB(null);
      fallo(e, 'No se pudo guardar el patrón de bloqueo', SIN_ANULAR.bloquear, () => guardarPB(true), confirmar);
    } finally {
      setOcupado(false);
    }
  };

  const activarPB = async (p, activo, confirmar = false) => {
    try {
      if (activo) {
        await editarPatronBloqueo(p.id, cuerpoPB({
          agendas: p.agenda_ids || [], dias: p.dias_semana || [], desde: p.desde, hasta: p.hasta,
          diaEntero: p.all_day !== false, start: p.start, end: p.end, motivo: p.motivo || '',
        }, { activo: true, actualizado_at: p.actualizado_at || null, ...(confirmar ? { confirmar: true } : {}) }));
      } else {
        if (!window.confirm('¿Quitar este patrón de bloqueo? Se quitan sus bloqueos de hoy en adelante; los de días ya pasados se quedan.')) return;
        await quitarPatronBloqueo(p.id);
      }
      toast.success(activo ? 'Patrón de bloqueo puesto de nuevo' : 'Patrón de bloqueo quitado');
      setConfirmacion(null);
      recargar();
    } catch (e) {
      fallo(e, 'No se pudo cambiar el patrón de bloqueo', SIN_ANULAR.bloquear,
        activo ? () => activarPB(p, true, true) : null, confirmar);
    }
  };

  const fechaLarga = (f) => conMayuscula(format(strToDate(f), "EEEE d 'de' MMMM 'de' yyyy", { locale: es }));

  return (
    <div className="max-w-7xl mx-auto" data-testid="disponibilidad-page">
      <PageHeader
        title="Agendas disponibles"
        subtitle={doctor
          ? 'Tus días de consulta y quirófano, tus vacaciones y días libres, y los bloqueos de tus agendas (solo lectura)'
          : 'Qué días viene cada doctor, sus vacaciones y días libres, y los bloqueos de sus agendas'}
        actions={(
          <Button variant="outline" asChild data-testid="disp-volver-agenda">
            <Link to="/citas">Volver a la agenda</Link>
          </Button>
        )}
      />

      {/* Doctor y año */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div className="w-full sm:w-80">
          <Label htmlFor="disp-doctor">Doctor</Label>
          <select
            id="disp-doctor"
            value={elegido ? elegido.clave : ''}
            onChange={(e) => { setEditando(false); irA(e.target.value, anio); }}
            disabled={cargandoPros}
            className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
            data-testid="disp-doctor"
          >
            <option value="">{cargandoPros ? 'Cargando…' : 'Elige un doctor…'}</option>
            {profesionales.map((p) => <option key={p.clave} value={p.clave}>{p.nombre}</option>)}
          </select>
        </div>
        {elegido && (
          <div className="flex items-center gap-1.5">
            <Button variant="outline" className="h-10 w-10 p-0" onClick={() => irA(elegido.clave, anio - 1)}
              disabled={anio <= ANIO_MIN} aria-label="Año anterior" data-testid="disp-anio-anterior">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-[4.5rem] text-center text-lg font-semibold tabular-nums text-[#13231E]"
              data-testid="disp-anio">{anio}</div>
            <Button variant="outline" className="h-10 w-10 p-0" onClick={() => irA(elegido.clave, anio + 1)}
              disabled={anio >= ANIO_MAX} aria-label="Año siguiente" data-testid="disp-anio-siguiente">
              <ChevronRight className="w-4 h-4" />
            </Button>
            {anio !== anioHoy && (
              <Button variant="ghost" className="h-10" onClick={() => irA(elegido.clave, anioHoy)}
                data-testid="disp-anio-actual">
                Año actual
              </Button>
            )}
          </div>
        )}
        {elegido && puedeEditar && (
          <Button
            variant={editando ? 'default' : 'outline'}
            className={`h-10 sm:ml-auto ${editando ? 'bg-[#13231E] hover:bg-[#13231E]/90 text-white' : ''}`}
            onClick={() => setEditando((v) => !v)}
            data-testid="disp-editar"
            aria-pressed={editando}
          >
            <Pencil className="w-4 h-4 mr-1.5" />
            {editando ? 'Terminar de editar' : 'Editar calendario'}
          </Button>
        )}
      </div>

      {cargandoPros ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando doctores…
        </div>
      ) : !profesionales.length ? (
        <p className="py-10 text-center text-sm text-muted-foreground" data-testid="disp-sin-doctores">
          {doctor
            ? 'No tienes ninguna agenda activa a tu nombre: aquí solo se ve la disponibilidad de tus propias agendas. Si falta alguna, pídeselo a administración.'
            : 'No hay doctores con agendas activas que puedas ver.'}
        </p>
      ) : !elegido ? (
        <div className="rounded-xl border border-dashed border-[#D9E3DD] py-12 text-center text-sm text-muted-foreground"
          data-testid="disp-elige">
          <CalendarRange className="w-6 h-6 mx-auto mb-2 text-[#13231E]/40 dark:text-white/40" />
          Elige un doctor para ver su calendario del año.
        </div>
      ) : (
        <>
          {/* Leyenda y resumen */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#13231E] mb-3" data-testid="disp-leyenda">
            <Muestra clase="disp-consulta">Consulta</Muestra>
            <Muestra clase="disp-quirofano">Quirófano</Muestra>
            <Muestra clase="disp-ambas">Consulta y quirófano</Muestra>
            <Muestra clase="disp-rojo" testId="disp-leyenda-rojo">Vacaciones, día libre o agenda bloqueada</Muestra>
            <span className="inline-flex items-center gap-1.5" data-testid="disp-leyenda-parcial">
              <span className="relative inline-block w-4 h-4 rounded border border-[#D9E3DD] bg-white overflow-hidden" aria-hidden="true">
                <span className="absolute inset-x-0 bottom-0 h-1 bg-[#b91c1c]" />
              </span>
              Unas horas o una agenda bloqueada
            </span>
            <Muestra clase={null}>Sin disponibilidad</Muestra>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 h-4 rounded ring-2 ring-[#13231E] dark:ring-white" aria-hidden="true" />
              Hoy
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="relative inline-block w-4 h-4 rounded border border-[#D9E3DD] bg-white" aria-hidden="true">
                <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-[#13231E] dark:bg-white" />
              </span>
              Cambiado a mano (no sigue el patrón)
            </span>
            {vigente && (
              <span className="sm:ml-auto text-muted-foreground" data-testid="disp-resumen">
                {anio}: {resumen.consulta} días de consulta · {resumen.quirofano} de quirófano
                {resumen.fuera ? ` · ${resumen.fuera} de vacaciones o libres` : ''}
              </span>
            )}
          </div>

          {editando && (
            <div className="rounded-lg border border-[#13231E]/30 bg-[#13231E]/[0.04] dark:border-white/30 dark:bg-white/[0.06] px-3 py-2 text-sm text-[#13231E] mb-3"
              data-testid="disp-aviso-edicion">
              ▸ Modo edición: pincha un día para marcar consulta, quirófano, vacaciones o día libre,
              o para bloquear una agenda ese día. Pulsa «Terminar de editar» para volver a abrir la
              agenda al pinchar un día.
            </div>
          )}

          {/* El año */}
          <div className={`relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 ${cargando ? 'opacity-60' : ''}`}
            data-testid="disp-calendario" aria-busy={cargando}>
            {MESES.map((nombreMes, mes) => (
              <section key={nombreMes} className="rounded-xl border border-[#D9E3DD] bg-white p-2.5 min-w-0"
                data-testid={`disp-mes-${mes + 1}`}>
                <h2 className="text-sm font-semibold capitalize text-[#13231E] mb-1.5 px-0.5">{nombreMes}</h2>
                <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground mb-1">
                  {INICIAL_DIA.map((d) => <div key={d}>{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {semanasDelMes(anio, mes).flat().map((f, i) => {
                    if (!f) return <div key={`h-${mes}-${i}`} />;
                    const d = dias[f];
                    const aspecto = aspectoDelDia(d, diasBloq[f]);
                    const color = aspecto.estado !== 'ninguna';
                    const manual = esManual(d);
                    const esHoy = f === hoy;
                    const clicable = editando || color || aspecto.parcial;
                    const numero = Number(f.slice(8, 10));
                    return (
                      <button
                        key={f}
                        type="button"
                        disabled={!clicable}
                        onClick={() => pincharDia(f)}
                        data-testid={`disp-dia-${f}`}
                        data-estado={aspecto.estado}
                        data-manual={manual ? 'si' : 'no'}
                        data-parcial={aspecto.parcial ? 'si' : 'no'}
                        aria-label={`${numero} de ${nombreMes}: ${aspecto.texto}${aspecto.parcial ? ', con horas o una agenda bloqueadas' : ''}${manual ? ', cambiado a mano' : ''}${esHoy ? ', hoy' : ''}`}
                        // El título dice también lo que viene con sus horas
                        // («consulta 09:00–14:00»): al pasar el ratón se ve
                        // de cuánto a cuánto sin abrir el día.
                        title={!clicable ? undefined
                          : editando ? (color ? conMayuscula(aspecto.texto) : undefined)
                            : `Abrir la agenda del ${textoFecha(f)}${color ? ` · ${aspecto.texto}` : ''}`}
                        className={`relative h-9 sm:h-8 w-full min-w-0 overflow-hidden rounded text-xs tabular-nums transition-colors
                          ${color ? `${aspecto.clase} font-semibold` : 'text-[#13231E]/80'}
                          ${clicable ? 'cursor-pointer' : 'cursor-default'}
                          ${clicable && !color ? 'hover:bg-[#13231E]/[0.06] dark:hover:bg-white/10' : ''}
                          ${clicable && color ? 'hover:brightness-110' : ''}
                          ${esHoy ? 'ring-2 ring-offset-1 ring-offset-background ring-[#13231E] dark:ring-white' : ''}`}
                      >
                        {numero}
                        {manual && (
                          <span
                            className={`absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${color ? 'bg-white ring-1 ring-black/30' : 'bg-[#13231E] dark:bg-white'}`}
                            aria-hidden="true"
                            data-testid={`disp-manual-${f}`}
                          />
                        )}
                        {aspecto.parcial && (
                          <span
                            className={`absolute inset-x-0 bottom-0 h-1 ${aspecto.clase === 'disp-rojo' ? 'bg-white/80' : 'bg-[#b91c1c]'}`}
                            aria-hidden="true"
                            data-testid={`disp-parcial-${f}`}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
            {cargando && (
              <div className="absolute inset-0 flex items-start justify-center pt-10 pointer-events-none">
                <Loader2 className="w-5 h-5 animate-spin text-[#13231E]/60 dark:text-white/60" />
              </div>
            )}
          </div>

          {/* Vacaciones y días libres del año */}
          <section className="mt-6 rounded-xl border border-[#D9E3DD] bg-white p-3 sm:p-4" data-testid="disp-ausencias">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div>
                <h2 className="text-base font-semibold text-[#13231E]">Vacaciones y días libres</h2>
                <p className="text-xs text-muted-foreground">
                  Bloquean todas sus agendas esos días; las citas que ya hubiera no se anulan.
                </p>
              </div>
              {puedeEditar && (
                <Button variant="outline" onClick={() => setFormAusencia({ tipo: 'vacaciones', desde: hoy, hasta: hoy })}
                  data-testid="disp-ausencia-nueva">
                  <Plus className="w-4 h-4 mr-1.5" /> Marcar vacaciones o día libre
                </Button>
              )}
            </div>
            {!ausencias.length ? (
              <p className="text-sm text-muted-foreground py-2" data-testid="disp-sin-ausencias">
                Sin vacaciones ni días libres en {anio}.
              </p>
            ) : (
              <ul className="divide-y divide-[#D9E3DD]">
                {ausencias.map((a) => (
                  <li key={a.grupo_id} className="flex flex-col sm:flex-row sm:items-center gap-2 py-2"
                    data-testid={`disp-ausencia-${a.grupo_id}`}>
                    <div className="min-w-0 flex-1 text-sm text-[#13231E]">
                      <span className="inline-block rounded px-1.5 py-0.5 text-xs font-semibold mr-2 align-middle disp-rojo">
                        {TEXTO_AUSENCIA[a.ausencia] || a.ausencia}
                      </span>
                      <span className="font-medium">{textoTramo(a.desde, a.hasta)}</span>
                      <span className="text-muted-foreground">
                        {' '}· {a.dias === 1 ? '1 día' : `${a.dias} días`}
                        {a.agendas?.length ? ` · ${a.agendas.join(', ')}` : ''}
                      </span>
                      {/* Una agenda del doctor dada de alta después: aún se
                          puede citar en ella esos días. */}
                      {a.faltan?.length > 0 && (
                        <span className="text-[#b91c1c]" data-testid={`disp-ausencia-faltan-${a.grupo_id}`}>
                          {' '}· Falta bloquear: {a.faltan.join(', ')}
                        </span>
                      )}
                    </div>
                    {/* Lo ya pasado se queda como estaba: ni se quita ni se completa. */}
                    {puedeEditar && a.hasta >= hoy && (
                      <div className="flex flex-wrap gap-1.5 shrink-0">
                        {a.faltan?.length > 0 && (
                          <Button variant="outline" size="sm" disabled={ocupado} onClick={() => completarBloqueo(a)}
                            data-testid={`disp-ausencia-completar-${a.grupo_id}`}>
                            <Lock className="w-3.5 h-3.5 mr-1" /> Completar el bloqueo
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => quitarAusenciaEntera(a)}
                          data-testid={`disp-ausencia-quitar-${a.grupo_id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Patrones */}
          <section className="mt-4 rounded-xl border border-[#D9E3DD] bg-white p-3 sm:p-4" data-testid="disp-patrones">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div>
                <h2 className="text-base font-semibold text-[#13231E]">Patrones de disponibilidad</h2>
                <p className="text-xs text-muted-foreground">
                  Rellenan los días solos. Un día cambiado a mano no rompe el patrón.
                </p>
              </div>
              {puedeEditar && (tieneConsulta || tieneQuirofano) && (
                <Button variant="outline" onClick={abrirAlta} data-testid="disp-patron-nuevo">
                  <Plus className="w-4 h-4 mr-1.5" /> Añadir patrón
                </Button>
              )}
            </div>
            {!patrones.length ? (
              <p className="text-sm text-muted-foreground py-2" data-testid="disp-sin-patrones">
                Aún no tiene patrones.{puedeEditar ? ' Añade uno para rellenar los días solos.' : ''}
              </p>
            ) : (
              <ul className="divide-y divide-[#D9E3DD]">
                {patrones.map((p) => {
                  const activo = p.activo !== false;
                  return (
                    <li key={p.id} className={`flex flex-col sm:flex-row sm:items-center gap-2 py-2 ${activo ? '' : 'opacity-60'}`}
                      data-testid={`disp-patron-${p.id}`}>
                      <div className="min-w-0 flex-1 text-sm text-[#13231E]">
                        <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold mr-2 align-middle ${
                          p.tipo === 'quirofano' ? 'disp-quirofano' : 'disp-consulta'}`}>
                          {p.tipo === 'quirofano' ? 'Quirófano' : 'Consulta'}
                        </span>
                        <span className="font-medium">
                          Todos los {textoDias(p.dias_semana)}{horasDelPatron(p) ? `, ${horasDelPatron(p)}` : ''}
                        </span>
                        <span className="text-muted-foreground">
                          {' '}· desde {textoFecha(p.desde)}
                          {p.hasta ? ` hasta ${textoFecha(p.hasta)}` : ' · sin fecha de fin'}
                          {p.nota ? ` · ${p.nota}` : ''}
                          {activo ? '' : ' · Desactivado'}
                        </span>
                      </div>
                      {puedeEditar && (
                        <div className="flex gap-1.5 shrink-0">
                          <Button variant="outline" size="sm" onClick={() => abrirEdicion(p)}
                            data-testid={`disp-patron-editar-${p.id}`}>
                            <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => activarPatron(p, !activo)}
                            data-testid={`disp-patron-${activo ? 'desactivar' : 'activar'}-${p.id}`}>
                            <Power className="w-3.5 h-3.5 mr-1" /> {activo ? 'Desactivar' : 'Activar'}
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 mt-5 mb-2 pt-4 border-t border-[#D9E3DD]">
              <div>
                <h2 className="text-base font-semibold text-[#13231E]">Patrones de bloqueo</h2>
                <p className="text-xs text-muted-foreground">
                  «Todos los lunes por la tarde, bloqueado», hasta 12 meses. Un día se puede quitar del
                  patrón (y volver) en el modo edición.
                </p>
              </div>
              {puedeEditar && (tieneConsulta || tieneQuirofano) && (
                <Button variant="outline" onClick={abrirAltaPB} data-testid="disp-patron-bloqueo-nuevo">
                  <Plus className="w-4 h-4 mr-1.5" /> Añadir patrón de bloqueo
                </Button>
              )}
            </div>
            {!patronesBloqueo.length ? (
              <p className="text-sm text-muted-foreground py-2" data-testid="disp-sin-patrones-bloqueo">
                Sin patrones de bloqueo.
              </p>
            ) : (
              <ul className="divide-y divide-[#D9E3DD]">
                {patronesBloqueo.map((p) => {
                  const activo = p.activo !== false;
                  return (
                    <li key={p.id} className={`flex flex-col sm:flex-row sm:items-center gap-2 py-2 ${activo ? '' : 'opacity-60'}`}
                      data-testid={`disp-patron-bloqueo-${p.id}`}>
                      <div className="min-w-0 flex-1 text-sm text-[#13231E]">
                        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold mr-2 align-middle disp-rojo">
                          <CalendarOff className="w-3 h-3" aria-hidden="true" /> Bloqueo
                        </span>
                        <span className="font-medium">Todos los {textoDias(p.dias_semana)}, {textoHoras(p)}</span>
                        <span className="text-muted-foreground">
                          {' '}· {(p.agendas || []).join(', ')}
                          {' '}· {textoTramo(p.desde, p.hasta)}
                          {p.motivo ? ` · ${p.motivo}` : ''}
                          {p.excepciones?.length ? ` · ${p.excepciones.length === 1 ? '1 día quitado' : `${p.excepciones.length} días quitados`}` : ''}
                          {activo ? '' : ' · Quitado'}
                        </span>
                      </div>
                      {puedeEditar && (
                        <div className="flex gap-1.5 shrink-0">
                          {activo && (
                            <Button variant="outline" size="sm" onClick={() => abrirEdicionPB(p)}
                              data-testid={`disp-patron-bloqueo-editar-${p.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => activarPB(p, !activo)}
                            data-testid={`disp-patron-bloqueo-${activo ? 'quitar' : 'activar'}-${p.id}`}>
                            <Power className="w-3.5 h-3.5 mr-1" /> {activo ? 'Quitar' : 'Volver a ponerlo'}
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}

      {/* Bloquear agendas sueltas (lo de «Agendas bloqueadas»): festivos,
          congresos, unas horas… de una agenda, varias o todas. Para gestión,
          con o sin doctor elegido. */}
      {gestion && !cargandoPros && todasLasAgendas.length > 0 && (
        <div className="mt-4">
          <BloqueosAgendaPanel
            agendas={todasLasAgendas}
            destacadas={profesional ? {
              nombre: profesional.nombre,
              ids: [...(profesional.consulta || []), ...(profesional.quirofano || [])].map((a) => a.id),
            } : null}
            recarga={recarga}
            onChanged={recargar}
          />
        </div>
      )}

      {/* Un día, en el modo edición */}
      <DiaDialog
        fecha={diaElegido}
        titulo={diaElegido ? fechaLarga(diaElegido) : ''}
        disp={diaElegido ? dias[diaElegido] : null}
        bloq={diaElegido ? diasBloq[diaElegido] : null}
        profesional={profesional}
        patrones={patronesBloqueo}
        ocupado={ocupado}
        onDisponibilidad={ponerDia}
        onAusencia={ausenciaDelDia}
        onQuitarAusencia={() => ausenciaDelDia(null, diaElegido)}
        onBloquear={bloquearDia}
        onQuitarBloqueo={quitarBloqueo}
        onVolverAlPatron={volverAlPatronDeBloqueo}
        onCerrar={() => setDiaElegido(null)}
      />

      <AusenciaDialog
        datos={formAusencia}
        setDatos={setFormAusencia}
        nombre={profesional?.nombre}
        ocupado={ocupado}
        onGuardar={() => guardarAusencia({
          profesional: profesional.clave, desde: formAusencia.desde,
          hasta: formAusencia.hasta || formAusencia.desde, tipo: formAusencia.tipo,
        })}
        onCerrar={() => setFormAusencia(null)}
      />

      <ConfirmarCitasDialog
        confirmacion={confirmacion}
        ocupado={ocupado}
        onConfirmar={() => confirmacion?.reintentar?.()}
        onCancelar={() => setConfirmacion(null)}
      />

      <PatronBloqueoDialog
        form={formPB}
        setForm={setFormPB}
        profesional={profesional}
        ocupado={ocupado}
        onGuardar={() => guardarPB()}
        onCerrar={() => setFormPB(null)}
      />

      {/* Alta o edición de un patrón de disponibilidad */}
      <Dialog open={!!form} onOpenChange={(v) => { if (!v) setForm(null); }}>
        <DialogContent className="max-w-md" data-testid="disp-patron-dialogo">
          <DialogHeader>
            <DialogTitle>{form?.id ? 'Editar patrón' : 'Añadir patrón'}</DialogTitle>
            <DialogDescription>
              {profesional?.nombre}: los días que marques se rellenan solos cada semana.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div>
                <Label>Tipo</Label>
                <div className="mt-1.5 flex gap-2" role="radiogroup" aria-label="Tipo">
                  {[['consulta', 'Consulta', VERDE_CONSULTA, '#052e16', tieneConsulta],
                    ['quirofano', 'Quirófano', VERDE_QUIROFANO, '#fff', tieneQuirofano]]
                    .filter(([valor, , , , hay]) => hay || form.tipo === valor)
                    .map(([valor, texto, color, letra]) => (
                      <button
                        key={valor}
                        type="button"
                        role="radio"
                        aria-checked={form.tipo === valor}
                        onClick={() => setForm((f) => ({ ...f, tipo: valor }))}
                        data-testid={`disp-form-tipo-${valor}`}
                        className={`px-3 py-1.5 rounded-md border text-sm font-medium ${
                          form.tipo === valor ? '' : 'border-[#D9E3DD] text-[#13231E]'}`}
                        style={form.tipo === valor ? { backgroundColor: color, borderColor: color, color: letra } : undefined}
                      >
                        {texto}
                      </button>
                    ))}
                </div>
              </div>
              <fieldset>
                <legend className="text-sm font-medium leading-none">Días de la semana</legend>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
                  {DIAS_SEMANA.map((nombre, i) => (
                    <label key={nombre} className="inline-flex items-center gap-2 text-sm text-[#13231E]">
                      <input
                        type="checkbox"
                        checked={form.dias.includes(i)}
                        onChange={() => alternarDiaSemana(i)}
                        className="h-4 w-4 accent-[#13231E]"
                        data-testid={`disp-form-dia-${i}`}
                      />
                      {nombre}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="disp-form-desde">Desde</Label>
                  <Input id="disp-form-desde" type="date" value={form.desde} className="mt-1.5"
                    onChange={(e) => setForm((f) => ({ ...f, desde: e.target.value }))}
                    data-testid="disp-form-desde" />
                </div>
                <div>
                  <Label htmlFor="disp-form-hasta">Hasta (opcional)</Label>
                  <Input id="disp-form-hasta" type="date" value={form.hasta} className="mt-1.5"
                    onChange={(e) => setForm((f) => ({ ...f, hasta: e.target.value }))}
                    data-testid="disp-form-hasta" />
                  <p className="text-[11px] text-muted-foreground mt-1">Vacío: sin fecha de fin.</p>
                </div>
              </div>
              {/* De cuánto a cuánto viene esos días (27/10/2026). Valen para
                  todos los días de la semana del patrón: horas distintas
                  según el día son dos patrones. Solo informan. */}
              <div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="disp-form-hora-inicio">Hora de inicio (opcional)</Label>
                    <Input id="disp-form-hora-inicio" type="time" value={form.horaInicio} className="mt-1.5 tabular-nums"
                      onChange={(e) => setForm((f) => ({ ...f, horaInicio: e.target.value }))}
                      data-testid="disp-form-hora-inicio" />
                  </div>
                  <div>
                    <Label htmlFor="disp-form-hora-fin">Hora de fin (opcional)</Label>
                    <Input id="disp-form-hora-fin" type="time" value={form.horaFin} className="mt-1.5 tabular-nums"
                      onChange={(e) => setForm((f) => ({ ...f, horaFin: e.target.value }))}
                      data-testid="disp-form-hora-fin" />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Sin horas: todo el día. Para horas distintas según el día, haz un patrón por tramo.
                  Las horas solo informan: no bloquean citas.
                </p>
                {horasMal(form.horaInicio, form.horaFin) && (
                  <p className="text-[11px] text-[#b91c1c] mt-1" data-testid="disp-form-horas-mal">
                    Pon las dos horas (o ninguna), con la de fin después de la de inicio.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="disp-form-nota">Nota (opcional)</Label>
                <Input id="disp-form-nota" value={form.nota} maxLength={300} className="mt-1.5"
                  onChange={(e) => setForm((f) => ({ ...f, nota: e.target.value }))}
                  placeholder="Lo que haga falta recordar de este patrón" data-testid="disp-form-nota" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setForm(null)} disabled={guardandoPatron}>Cancelar</Button>
            <Button onClick={guardarPatron}
              disabled={guardandoPatron || !form?.dias?.length || horasMal(form?.horaInicio, form?.horaFin)}
              data-testid="disp-form-guardar">
              {guardandoPatron && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {form?.id ? 'Guardar cambios' : 'Añadir patrón'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
